<template>
  <div>
    <div id="main" style="width: 100%; height: 400px"></div>
  </div>
</template>

<script>
import { getStorageCurve } from '@/api/VR/storage'

export default {
  name: 'StorageUsageChart',
  props: {
    storagePoolId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      currentMonth: 2, // 默认显示2个月(60天)
      myChart: null,
      updateTime: '',
      loading: false,
    }
  },
  computed: {
    // 根据月份计算天数
    currentDays() {
      return this.currentMonth * 30
    },
  },
  methods: {
    // 格式化日期显示为 月/日
    formatDisplayDate(dateStr) {
      const date = new Date(dateStr)
      return `${date.getMonth() + 1}/${date.getDate()}`
    },

    // 更新图表数据
    async updateChart() {
      try {
        this.loading = true

        // 先更新时间戳
        this.updateTimeStamp()

        // 调用接口获取数据
        const params = {
          month: this.currentMonth,
          storagePoolId: this.storagePoolId,
        }

        const response = await getStorageCurve(params)

        if (response && response.data) {
          this.processChartData(response.data)
        } else {
          console.error('接口返回数据格式异常:', response)
          this.showEmptyChart()
        }
      } catch (error) {
        console.error('获取存储使用曲线失败:', error)
        this.showEmptyChart()
      } finally {
        this.loading = false
      }
    },

    // 处理图表数据
    processChartData(apiData) {
      // 确保数据是按日期排序的
      const sortedData = apiData.sort((a, b) => new Date(a.statDay) - new Date(b.statDay))

      // 提取日期和存储量数据
      const dates = sortedData.map((item) => this.formatDisplayDate(item.statDay))
      const data = sortedData.map((item) => (item.totalStorageMb / 1024).toFixed(2)) // 转换为GB

      this.renderChart(dates, data)
    },

    // 渲染图表
    renderChart(dates, data) {
      const option = {
        title: {
          text: `存储使用详情(最近${this.currentDays}天)`,
        },
        tooltip: {
          trigger: 'axis',
          formatter: function (params) {
            const date = params[0].axisValue
            const value = params[0].data
            return `${date}: ${value} GB`
          },
        },
        grid: {
          left: '5%', // 增加左边距，为Y轴标签留出更多空间
          right: '0',
          bottom: '15%',
          top: '15%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: dates,
          axisLabel: {
            interval: 0,
            rotate: this.currentDays > 30 ? 45 : 0,
            margin: 10,
          },
        },
        yAxis: {
          type: 'value',
          name: '存储使用量(GB)',
          nameTextStyle: {
            padding: [0, 0, 0, 10], // 给Y轴名称添加内边距
          },
          axisLabel: {
            margin: 8, // 增加Y轴数值标签的边距
          },
        },
        series: [
          {
            name: '存储使用量',
            type: 'line',
            data: data,
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  {
                    offset: 0,
                    color: 'rgba(59, 130, 246, 0.6)',
                  },
                  {
                    offset: 1,
                    color: 'rgba(59, 130, 246, 0.1)',
                  },
                ],
              },
            },
            lineStyle: {
              color: '#3B82F6',
              width: 2,
            },
            itemStyle: {
              color: '#3B82F6',
            },
            smooth: true,
          },
        ],
        graphic: [
          {
            type: 'group',
            left: '5%',
            bottom: '5%',
            children: [
              {
                type: 'rect',
                left: 'center',
                top: 'middle',
                shape: {
                  width: 60,
                  height: 30,
                },
                style: {
                  fill: this.currentMonth === 1 ? '#409eff' : '#f5f7fa',
                  stroke: '#409eff',
                  lineWidth: 1,
                },
              },
              {
                type: 'text',
                left: 'center',
                top: 'middle',
                style: {
                  text: '30天',
                  fill: this.currentMonth === 1 ? '#fff' : '#409eff',
                  fontSize: 14,
                },
              },
            ],
            onclick: () => {
              this.switchMonth(1)
            },
          },
          {
            type: 'group',
            left: '12%',
            bottom: '5%',
            children: [
              {
                type: 'rect',
                left: 'center',
                top: 'middle',
                shape: {
                  width: 60,
                  height: 30,
                },
                style: {
                  fill: this.currentMonth === 2 ? '#409eff' : '#f5f7fa',
                  stroke: '#409eff',
                  lineWidth: 1,
                },
              },
              {
                type: 'text',
                left: 'center',
                top: 'middle',
                style: {
                  text: '60天',
                  fill: this.currentMonth === 2 ? '#fff' : '#409eff',
                  fontSize: 14,
                },
              },
            ],
            onclick: () => {
              this.switchMonth(2)
            },
          },
          {
            type: 'group',
            left: '19%',
            bottom: '5%',
            children: [
              {
                type: 'rect',
                left: 'center',
                top: 'middle',
                shape: {
                  width: 60,
                  height: 30,
                },
                style: {
                  fill: this.currentMonth === 3 ? '#409eff' : '#f5f7fa',
                  stroke: '#409eff',
                  lineWidth: 1,
                },
              },
              {
                type: 'text',
                left: 'center',
                top: 'middle',
                style: {
                  text: '90天',
                  fill: this.currentMonth === 3 ? '#fff' : '#409eff',
                  fontSize: 14,
                },
              },
            ],
            onclick: () => {
              this.switchMonth(3)
            },
          },
          {
            type: 'group',
            right: '5%',
            bottom: '5%',
            children: [
              {
                type: 'circle',
                left: 0,
                top: 'middle',
                shape: {
                  cx: 0,
                  cy: 0,
                  r: 8,
                },
                style: {
                  fill: '#909399',
                },
              },
              {
                type: 'text',
                left: 6,
                top: 'middle',
                style: {
                  text: '!',
                  fill: '#fff',
                  fontSize: 10,
                  fontWeight: 'bold',
                  textAlign: 'center',
                  textVerticalAlign: 'middle',
                },
              },
              {
                type: 'text',
                left: 25,
                top: 'middle',
                style: {
                  text: `数据更新时间: ${this.updateTime}`,
                  fill: '#999',
                  fontSize: 12,
                },
              },
            ],
          },
        ],
      }

      this.myChart.setOption(option, true)
    },

    // 显示空数据图表
    showEmptyChart() {
      // 生成空数据
      const emptyDates = []
      const emptyData = []
      const today = new Date()

      for (let i = this.currentDays - 1; i >= 0; i--) {
        const date = new Date()
        date.setDate(today.getDate() - i)
        emptyDates.push(this.formatDisplayDate(date))
        emptyData.push(0)
      }

      this.renderChart(emptyDates, emptyData)
    },

    // 切换月份
    switchMonth(month) {
      this.currentMonth = month
      this.updateChart()
    },

    // 更新时间戳
    updateTimeStamp() {
      const now = new Date()
      this.updateTime = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now
        .getDate()
        .toString()
        .padStart(2, '0')} ${now.getHours().toString().padStart(2, '0')}:${now
        .getMinutes()
        .toString()
        .padStart(2, '0')}`
    },

    // 初始化图表
    initChart() {
      this.myChart = this.$echarts.init(document.getElementById('main'))
      // 初始设置时间戳
      this.updateTimeStamp()
      this.updateChart()

      // 窗口大小变化时重绘图表
      window.addEventListener('resize', () => {
        this.myChart.resize()
      })
    },
  },
  mounted() {
    this.initChart()
  },
  watch: {
    // 监听storagePoolId变化，重新加载图表数据
    storagePoolId: {
      handler() {
        if (this.myChart) {
          this.updateChart()
        }
      },
      immediate: false,
    },
  },
  beforeDestroy() {
    // 组件销毁时移除事件监听
    window.removeEventListener('resize', () => {
      this.myChart.resize()
    })
    if (this.myChart) {
      this.myChart.dispose()
    }
  },
}
</script>

<style scoped></style>
